import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { compareSemver } from "@veil/shared/version";
import { Alert, AlertDescription } from "@veil/ui/components/alert";
import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@veil/ui/components/empty";
import { Input } from "@veil/ui/components/input";
import { Skeleton } from "@veil/ui/components/skeleton";
import { Switch } from "@veil/ui/components/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@veil/ui/components/table";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@veil/ui/components/tabs";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@veil/ui/components/tooltip";
import {
	DataTable,
	DataTableColumnHeader,
	DataTableToolbar,
	type ExtendedColumnSort,
	getSortingStateParser,
	type Option,
	useDataTable,
} from "@veil/ui/data-table";
import { formatRelative } from "@veil/ui/lib/format";
import { cn } from "@veil/ui/lib/utils";
import AlertTriangle from "lucide-react/dist/esm/icons/alert-triangle";
import CheckCircle2 from "lucide-react/dist/esm/icons/check-circle-2";
import CpuIcon from "lucide-react/dist/esm/icons/cpu";
import Download from "lucide-react/dist/esm/icons/download";
import HardDrive from "lucide-react/dist/esm/icons/hard-drive";
import Info from "lucide-react/dist/esm/icons/info";
import Loader2 from "lucide-react/dist/esm/icons/loader-2";
import Monitor from "lucide-react/dist/esm/icons/monitor";
import Network from "lucide-react/dist/esm/icons/network";
import Plus from "lucide-react/dist/esm/icons/plus";
import Search from "lucide-react/dist/esm/icons/search";
import Server from "lucide-react/dist/esm/icons/server";
import XCircle from "lucide-react/dist/esm/icons/x-circle";
import {
	parseAsArrayOf,
	parseAsInteger,
	parseAsString,
	useQueryState,
} from "nuqs";
import { useEffect, useMemo, useRef, useState } from "react";
import {
	Area,
	AreaChart,
	CartesianGrid,
	Tooltip as RechartsTooltip,
	ResponsiveContainer,
	XAxis,
	YAxis,
} from "recharts";
import { toast } from "sonner";
import { Link, useLocation } from "wouter";
import { EmptyState } from "@/components/query-state";
import { useClusterState } from "@/components/render-status-bar";
import { BundleSyncCard } from "@/components/workers/bundle-sync";
import { CpuStrip } from "@/components/workers/cpu-strip";
import { DeleteWorkerDialog } from "@/components/workers/delete-worker-dialog";
import { DriftBadge } from "@/components/workers/drift-badge";
import { HostFormDialog } from "@/components/workers/host-form-dialog";
import { LaunchProfileCard } from "@/components/workers/launch-profile-card";
import { LoadReadout } from "@/components/workers/load-readout";
import { MemoryBar } from "@/components/workers/memory-bar";
import { PeakConcurrentStat } from "@/components/workers/peak-concurrent-stat";
import { WorkerPoolCell } from "@/components/workers/pool-cell";
import { RangeFilter } from "@/components/workers/range-filter";
import { RouteDriftNote } from "@/components/workers/route-drift-note";
import {
	WorkerStatusCell,
	workerStatus,
	workerStatusRank,
} from "@/components/workers/status-cell";
import {
	StopSessionDialog,
	type StopSessionTarget,
} from "@/components/workers/stop-session-dialog";
import { UsageBar } from "@/components/workers/usage-bar";
import { UtilCell } from "@/components/workers/util-cell";
import { WorkerRowActions } from "@/components/workers/worker-row-actions";
import { WorkerViewTabs } from "@/components/workers/worker-view-tabs";
import { WorkersCommandBar } from "@/components/workers/workers-command-bar";
import { api, unwrap } from "@/lib/api";
import { hasRole, useAuth } from "@/lib/auth-context";
import { FLEET_STATUS_REFETCH_MS } from "@/lib/fleet-intervals";
import {
	describeSession,
	fingerprintFromBundlePath,
	shortId,
} from "@/lib/session-identity";
import {
	type FleetHistorySnapshot,
	useFleetHostHistory,
} from "@/lib/use-fleet-host-history";
import { useFleetLive } from "@/lib/use-fleet-live";
import { useFleetRange } from "@/lib/use-fleet-range";
import type { WorkerRow, WorkersResponse } from "@/lib/workers/types";
import { detectedLocationLabel } from "@/pages/cluster/geo-routing";

type Snapshot = FleetHistorySnapshot;

const OS_LABELS: Record<string, string> = {
	win: "Windows",
	mac: "macOS",
	linux: "Linux",
	android: "Android",
};

/**
 * What this worker can actually launch, by browser major.
 *
 * The worker reports three maps all keyed by major — builds, profile bundles,
 * and the OSes those bundles cover — and the page used to render each one as
 * `JSON.stringify(..., null, 2)` in a `<pre>`. That is the worker's data
 * structure, not the operator's question, which is "what can this box run".
 * Merging the three on their shared key answers it in one row per major.
 */
function InstalledInventory({ latest }: { latest: Snapshot }) {
	const rows = useMemo(() => {
		const builds = latest.installedBuilds ?? {};
		const profiles = latest.installedProfiles ?? {};
		const oses = latest.installedProfileOses ?? {};
		const majors = [
			...new Set([
				...Object.keys(builds),
				...Object.keys(profiles),
				...Object.keys(oses),
			]),
		]
			.map((key) => Number.parseInt(key, 10))
			.filter((n) => Number.isFinite(n))
			.sort((a, b) => b - a);

		return majors.map((major) => {
			const key = String(major);
			return {
				major,
				builds: builds[key] ?? [],
				profiles: profiles[key] ?? [],
				oses: oses[key] ?? [],
			};
		});
	}, [
		latest.installedBuilds,
		latest.installedProfiles,
		latest.installedProfileOses,
	]);

	// An older worker reports no OS breakdown at all. Absent is not the same as
	// empty, and the column would otherwise read as "no profiles" on every row.
	const osesUnknown = latest.installedProfileOses === null;

	if (rows.length === 0) {
		return (
			<p className="text-sm text-muted-foreground">
				This worker reports no installed browser builds or profile bundles, so
				it cannot serve a launch yet.
			</p>
		);
	}

	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead className="w-20">Major</TableHead>
					<TableHead>Browser build</TableHead>
					<TableHead>Profile bundle</TableHead>
					<TableHead>Profiles for</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{rows.map((row) => (
					<TableRow key={row.major}>
						<TableCell className="font-mono tabular-nums">
							{row.major}
						</TableCell>
						<TableCell className="font-mono text-xs">
							{row.builds.length > 0 ? row.builds.join(", ") : "not installed"}
						</TableCell>
						<TableCell className="font-mono text-xs">
							{row.profiles.length > 0
								? row.profiles.join(", ")
								: "not installed"}
						</TableCell>
						<TableCell className="text-xs">
							{osesUnknown ? (
								<span className="text-muted-foreground">not reported</span>
							) : row.oses.length > 0 ? (
								<div className="flex flex-wrap gap-1">
									{row.oses.map((os) => (
										<Badge key={os} variant="outline" className="text-[10px]">
											{OS_LABELS[os] ?? os}
										</Badge>
									))}
								</div>
							) : (
								<span className="text-muted-foreground">none</span>
							)}
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
}

/** Convert the worker's raw /proc/meminfo KB payload into the GiB-based shape MemoryBar expects. */
function toMemMetrics(
	raw:
		| PrismaJson.FleetMemMetrics
		| import("@/lib/use-fleet-live").FleetLiveMem
		| null
		| undefined,
): import("@/components/workers/memory-bar").MemMetrics | null {
	if (!raw) return null;
	const KB = 1024 * 1024; // 1 GiB = 1024 * 1024 KiB
	const usedKB = raw.totalKB - raw.availableKB;
	return {
		totalGiB: raw.totalKB / KB,
		usedGiB: usedKB / KB,
		buffersGiB: raw.buffersKB ? raw.buffersKB / KB : undefined,
		cachedGiB: raw.cachedKB ? raw.cachedKB / KB : undefined,
		shmemGiB: raw.shmemKB ? raw.shmemKB / KB : undefined,
		swapTotalGiB: raw.swapTotalKB ? raw.swapTotalKB / KB : undefined,
		swapUsedGiB:
			raw.swapTotalKB && raw.swapFreeKB
				? (raw.swapTotalKB - raw.swapFreeKB) / KB
				: undefined,
		source: raw.source,
	};
}

function cpuValue(snapshot: Snapshot | null): number {
	if (!snapshot?.runtime) return -1;
	const cpu = snapshot.runtime.cpu?.aggregate.totalPct;
	if (typeof cpu === "number") return cpu;
	return snapshot.runtime.loadAvg1m * 10; // soft fallback for ordering only
}

/** Used memory in binary GiB, derived from the raw /proc/meminfo payload. */
function memUsedGiB(rt: Snapshot["runtime"] | undefined): number | null {
	const mem = rt?.mem;
	if (!mem || mem.totalKB <= 0) return null;
	return (mem.totalKB - mem.availableKB) / (1024 * 1024);
}

/** Total memory in binary GiB, derived from the raw /proc/meminfo payload. */
function memTotalGiB(rt: Snapshot["runtime"] | undefined): number | null {
	const mem = rt?.mem;
	if (!mem || mem.totalKB <= 0) return null;
	return mem.totalKB / (1024 * 1024);
}

function memPct(snapshot: Snapshot | null): number {
	const used = memUsedGiB(snapshot?.runtime);
	const total = memTotalGiB(snapshot?.runtime);
	if (used == null || total == null || total <= 0) return -1;
	return (used / total) * 100;
}

function diskPctUsed(snapshot: Snapshot | null): number {
	const total = snapshot?.diskTotalGB;
	const free = snapshot?.diskFreeGB;
	if (typeof total !== "number" || total <= 0 || typeof free !== "number") {
		return -1;
	}
	return ((total - free) / total) * 100;
}

function sessionsValue(snapshot: Snapshot | null): number {
	return snapshot?.runtime?.activeSessions ?? -1;
}

function ReachabilityBadge({ snapshot }: { snapshot: Snapshot | null }) {
	if (!snapshot) {
		return <span className="text-xs text-muted-foreground">—</span>;
	}
	if (snapshot.reachable) {
		return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
	}
	if (snapshot.unreachableReason === "api-dead") {
		return (
			<span
				className="inline-flex items-center gap-1"
				title="Host is up but local-api is not responding"
			>
				<AlertTriangle className="h-4 w-4 text-amber-500" />
				<Badge
					variant="outline"
					className="text-[10px] text-amber-600 border-amber-400"
				>
					API offline
				</Badge>
			</span>
		);
	}
	return (
		<span
			className="inline-flex items-center gap-1"
			title="Host is unreachable"
		>
			<XCircle className="h-4 w-4 text-destructive" />
			<Badge
				variant="outline"
				className="text-[10px] text-destructive border-destructive/50"
			>
				host down
			</Badge>
		</span>
	);
}

function LoadIndicator({ load }: { load: number }) {
	const color =
		load < 1
			? "text-emerald-600"
			: load < 4
				? "text-amber-600"
				: "text-destructive";
	return (
		<span className={`font-mono text-xs ${color}`}>{load.toFixed(2)}</span>
	);
}

// ---------------------------------------------------------------------------
// Display pool — per-core Xvfb status from the worker's DisplayManager.
// Renders only when the worker shipped a `displays` block; older snapshots
// (pre-DisplayManager) simply omit this section. Live SSE data wins over the
// persisted snapshot — same pattern as the rest of the Runtime card.
// ---------------------------------------------------------------------------
type DisplayPoolData =
	| import("@/lib/use-fleet-host-history").FleetDisplays
	| import("@/lib/use-fleet-live").FleetLiveDisplays;

function DisplayPool({
	live,
	snapshot,
}: {
	live: DisplayPoolData | undefined;
	snapshot: DisplayPoolData | undefined;
}) {
	const pool = live ?? snapshot;
	if (!pool) return null;

	if (pool.disabled) {
		return (
			<div className="mt-4 rounded border border-dashed px-3 py-2 text-xs text-muted-foreground flex items-start gap-2">
				<Monitor className="h-3.5 w-3.5 mt-[2px] shrink-0" />
				<span>
					Xvfb pool disabled on this host — headful sessions won't be routed to
					a managed display. Install the <code>xvfb</code> package and restart{" "}
					<code>veil-api</code> to enable.
				</span>
			</div>
		);
	}

	if (pool.displays.length === 0) {
		return null;
	}

	const total = pool.displays.reduce((sum, d) => sum + d.sessions, 0);
	const adoptedCount = pool.displays.filter((d) => d.adopted).length;

	return (
		<div className="mt-4">
			<div className="flex items-baseline justify-between mb-2">
				<div className="text-xs uppercase text-muted-foreground flex items-center gap-1.5">
					<Monitor className="h-3 w-3" />
					Display pool
				</div>
				<div className="text-xs text-muted-foreground tabular-nums">
					{pool.displays.length} displays · {total} active session
					{total === 1 ? "" : "s"}
					{adoptedCount > 0 ? ` · ${adoptedCount} adopted` : ""}
				</div>
			</div>
			<div className="flex flex-wrap gap-1.5">
				{pool.displays
					.slice()
					.sort((a, b) => a.display - b.display)
					.map((d) => (
						<DisplayChip key={d.display} info={d} />
					))}
			</div>
		</div>
	);
}

function DisplayChip({
	info,
}: {
	info: { display: number; pid: number; adopted: boolean; sessions: number };
}) {
	const busy = info.sessions > 0;
	return (
		<TooltipProvider delayDuration={150}>
			<Tooltip>
				<TooltipTrigger asChild>
					<div
						className={cn(
							"inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs",
							"transition-colors",
							busy
								? "border-emerald-500/40 bg-emerald-500/5"
								: "border-border bg-muted/40",
						)}
					>
						<span
							className={cn(
								"h-1.5 w-1.5 rounded-full",
								busy ? "bg-emerald-500" : "bg-muted-foreground/40",
							)}
							aria-hidden="true"
						/>
						<span className="font-mono">:{info.display}</span>
						<span className="text-muted-foreground/70">·</span>
						<span className="tabular-nums font-semibold">{info.sessions}</span>
					</div>
				</TooltipTrigger>
				<TooltipContent side="top" className="text-xs">
					<div className="space-y-0.5">
						<div className="font-mono">
							:{info.display}{" "}
							<span className="text-muted-foreground">(pid {info.pid})</span>
						</div>
						<div>
							{info.sessions} session{info.sessions === 1 ? "" : "s"} ·{" "}
							<span className="text-muted-foreground">
								{info.adopted ? "adopted" : "spawned"}
							</span>
						</div>
					</div>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}

export function Workers({ hostname }: { hostname?: string }) {
	if (hostname) return <WorkerDetail hostname={hostname} />;
	return <WorkersOverview />;
}

/** Big enough that a real fleet fits on one page; the pager exists for the rest. */
const DEFAULT_PAGE_SIZE = 50;

const DEFAULT_SORTING: ExtendedColumnSort<WorkerRow>[] = [
	{ id: "hostname", desc: false },
];

const SORTABLE_COLUMN_IDS = new Set([
	"hostname",
	"status",
	"placement",
	"pool",
	"sessions",
	"cpu",
	"memory",
	"disk",
]);

/**
 * The statuses worth filtering to, worst first.
 *
 * Declared rather than faceted off the rows: the useful question is "show me the
 * broken ones", and an option that disappears the moment nothing is broken is
 * exactly the option an operator goes looking for.
 */
const STATUS_OPTIONS: Option[] = [
	{ label: "Container missing", value: "missing" },
	{ label: "Container exited", value: "crashed" },
	{ label: "Host down", value: "host-down" },
	{ label: "API offline", value: "api-dead" },
	{ label: "No data", value: "unknown" },
	{ label: "Stopped", value: "stopped" },
	{ label: "Starting", value: "starting" },
	{ label: "Reachable", value: "ok" },
];

/**
 * Where the worker runs.
 *
 * Both options always offered, for the same reason the statuses are: "show me
 * only the remote ones" is the question, and it is asked on a fleet where every
 * worker might be remote.
 */
const PLACEMENT_OPTIONS: Option[] = [
	{ label: "On this machine", value: "local" },
	{ label: "On another machine", value: "remote" },
];

/** Sort value per column, so one comparator covers every sortable column. */
function sortValue(row: WorkerRow, columnId: string): number | string {
	switch (columnId) {
		case "status":
			return workerStatusRank(row);
		case "placement":
			return row.placement;
		case "pool":
			// Drained workers first when ascending: "which of these is not taking
			// traffic" is the question this column answers.
			return (row.enabled ? 1000 : 0) + row.weight;
		case "sessions":
			return sessionsValue(row.snapshot);
		case "cpu":
			return cpuValue(row.snapshot);
		case "memory":
			return memPct(row.snapshot);
		case "disk":
			return diskPctUsed(row.snapshot);
		default:
			return row.hostname;
	}
}

function compareWorkers(
	a: WorkerRow,
	b: WorkerRow,
	sorting: SortingState,
): number {
	for (const sort of sorting) {
		const av = sortValue(a, sort.id);
		const bv = sortValue(b, sort.id);
		let cmp: number;
		if (typeof av === "string" || typeof bv === "string") {
			cmp = String(av).localeCompare(String(bv), undefined, {
				numeric: true,
				sensitivity: "base",
			});
		} else {
			cmp = av - bv;
		}
		if (cmp !== 0) return sort.desc ? -cmp : cmp;
	}
	// Stable tail-break, so equal rows never reshuffle between polls.
	return a.hostname.localeCompare(b.hostname, undefined, { numeric: true });
}

function WorkersOverview() {
	const queryClient = useQueryClient();
	const { user } = useAuth();
	const canEdit = hasRole(user, "operator");
	const [, navigate] = useLocation();

	const workers = useQuery({
		queryKey: ["workers"],
		queryFn: () => unwrap(api.api.workers.get()) as Promise<WorkersResponse>,
		refetchInterval: FLEET_STATUS_REFETCH_MS,
		// Keep the populated table on screen during background refetches so
		// the ~10s poll never flashes the loading/empty state.
		placeholderData: keepPreviousData,
	});
	const cluster = useClusterState();

	const hosts = workers.data?.hosts ?? [];

	const pollNow = useMutation({
		mutationFn: () => unwrap(api.api.workers["poll-now"].post()),
		onSuccess: () => {
			toast.success("Polling workers…");
			queryClient.invalidateQueries({ queryKey: ["workers"] });
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Poll failed"),
	});

	const [editing, setEditing] = useState<WorkerRow | null>(null);
	const [removing, setRemoving] = useState<WorkerRow | null>(null);

	// useDataTable owns these keys; reading them here is how the filtering,
	// sorting and paging get applied, because the hook is built for a server that
	// does that work and this list already lives in memory. Same nuqs keys, so
	// both views of the URL stay identical (see Operations.tsx for the pattern
	// against a real endpoint).
	const [page] = useQueryState("page", parseAsInteger.withDefault(1));
	const [perPage] = useQueryState(
		"perPage",
		parseAsInteger.withDefault(DEFAULT_PAGE_SIZE),
	);
	const [sorting] = useQueryState(
		"sort",
		getSortingStateParser<WorkerRow>(SORTABLE_COLUMN_IDS).withDefault(
			DEFAULT_SORTING,
		),
	);
	const [statusFilter] = useQueryState(
		"status",
		parseAsArrayOf(parseAsString, ","),
	);
	const [placementFilter] = useQueryState(
		"placement",
		parseAsArrayOf(parseAsString, ","),
	);
	// The text search is ours rather than a toolbar column filter: the shared
	// hook splits a filter value on every non-alphanumeric character, which turns
	// `local-worker-1` into three terms and renders them back into the input as
	// `local,worker,1`. Hostnames and IPs are made of exactly those characters.
	const [search, setSearch] = useQueryState(
		"q",
		parseAsString.withDefault("").withOptions({ clearOnDefault: true }),
	);
	const [, setPage] = useQueryState("page", parseAsInteger.withDefault(1));

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		const wanted = statusFilter?.length ? new Set(statusFilter) : null;
		const wantedPlacement = placementFilter?.length
			? new Set(placementFilter)
			: null;
		const out: WorkerRow[] = [];
		for (const h of hosts) {
			if (
				q &&
				!h.hostname.toLowerCase().includes(q) &&
				!h.publicIp.toLowerCase().includes(q)
			) {
				continue;
			}
			if (wanted && !wanted.has(workerStatus(h))) continue;
			if (wantedPlacement && !wantedPlacement.has(h.placement)) continue;
			out.push(h);
		}
		return out.sort((a, b) => compareWorkers(a, b, sorting));
	}, [hosts, search, statusFilter, placementFilter, sorting]);

	const pageRows = useMemo(
		() => filtered.slice((page - 1) * perPage, page * perPage),
		[filtered, page, perPage],
	);

	const columns = useMemo<ColumnDef<WorkerRow>[]>(
		() => [
			{
				id: "hostname",
				accessorKey: "hostname",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Worker" />
				),
				cell: ({ row }) => {
					const h = row.original;
					return (
						<div className="flex min-w-0 flex-col gap-0.5">
							<span className="flex min-w-0 items-center gap-1.5">
								<Link
									href={`/workers/${h.hostname}`}
									className="group inline-flex min-w-0 items-center gap-2 font-medium hover:underline"
								>
									<Server className="size-4 text-muted-foreground" />
									<span className="truncate">{h.hostname}</span>
								</Link>
								{/* Metadata, not status: where the worker runs does not
								    change while you are looking at it, so it gets the
								    quietest badge treatment on the row rather than a tone.
								    The word is the whole signal — a glyph distinguishing a
								    container from a server is the kind of thing an
								    operator has to learn twice. */}
								<Badge
									variant="outline"
									className="text-[10px] font-normal text-muted-foreground"
									title={
										h.placement === "local"
											? "Runs on this manager's machine"
											: "Runs on a machine of its own"
									}
								>
									{h.placement}
								</Badge>
							</span>
							<span className="font-mono text-[10px] tabular-nums text-muted-foreground">
								{h.publicIp}:{h.lbPort}
							</span>
							<RouteDriftNote worker={h} />
						</div>
					);
				},
				enableHiding: false,
			},
			{
				id: "status",
				// An accessor, not just a cell: TanStack gates `getCanFilter()` on one,
				// so a display-only column silently drops out of the toolbar however
				// its meta is declared — which is where this facet had been going.
				accessorFn: (row) => workerStatus(row),
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Status" />
				),
				cell: ({ row }) => <WorkerStatusCell row={row.original} />,
				enableColumnFilter: true,
				meta: {
					label: "Status",
					variant: "multiSelect",
					options: STATUS_OPTIONS,
				},
			},
			// Hidden by default: the badge in the Worker cell already answers this
			// per row. The column exists so the facet has something to bind to, and
			// so a mixed fleet can be sorted by it.
			{
				id: "placement",
				accessorKey: "placement",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Placement" />
				),
				cell: ({ row }) => (
					<span className="text-xs">
						{row.original.placement === "local"
							? "This machine"
							: "Another machine"}
					</span>
				),
				enableColumnFilter: true,
				meta: {
					label: "Placement",
					variant: "multiSelect",
					options: PLACEMENT_OPTIONS,
				},
			},
			{
				id: "region",
				accessorFn: (row) => row.effectiveRegion ?? "",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Region" />
				),
				cell: ({ row }) => (
					<span
						className="font-mono text-xs"
						title={detectedLocationLabel(row.original)}
					>
						{row.original.effectiveRegion ?? "—"}
						{row.original.effectiveRegion && !row.original.region ? (
							<span className="ml-1 font-sans text-muted-foreground">auto</span>
						) : null}
					</span>
				),
			},
			{
				id: "pool",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="In pool" />
				),
				cell: ({ row }) => (
					<WorkerPoolCell worker={row.original} canEdit={canEdit} />
				),
			},
			{
				id: "sessions",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Sessions" />
				),
				cell: ({ row }) => {
					const rt = row.original.snapshot?.runtime;
					if (!rt)
						return <span className="text-xs text-muted-foreground">—</span>;
					return (
						<Tooltip>
							<TooltipTrigger asChild>
								<span className="cursor-default font-mono text-xs tabular-nums">
									<span className="font-semibold text-foreground">
										{rt.activeSessions}
									</span>
									<span className="text-muted-foreground">
										{" "}
										/ {rt.sessionsToday}
									</span>
								</span>
							</TooltipTrigger>
							<TooltipContent side="bottom" className="text-[11px]">
								<div className="space-y-0.5">
									<div>{rt.activeSessions} active now</div>
									<div>{rt.sessionsToday} today</div>
									<div>{rt.sessionsLast24h} last 24h</div>
								</div>
							</TooltipContent>
						</Tooltip>
					);
				},
			},
			{
				id: "cpu",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="CPU" />
				),
				cell: ({ row }) => (
					<UtilCell runtime={row.original.snapshot?.runtime ?? null} />
				),
			},
			{
				id: "memory",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Memory" />
				),
				cell: ({ row }) => {
					const rt = row.original.snapshot?.runtime;
					const used = memUsedGiB(rt);
					const total = memTotalGiB(rt);
					if (!rt || used == null || total == null || total <= 0) {
						return <span className="text-xs text-muted-foreground">—</span>;
					}
					return (
						<Tooltip>
							<TooltipTrigger asChild>
								<span className="inline-block cursor-default">
									<UsageBar used={used} total={total} unit="GB" />
								</span>
							</TooltipTrigger>
							<TooltipContent side="bottom" className="text-[11px]">
								<MemTooltipContent runtime={rt} />
							</TooltipContent>
						</Tooltip>
					);
				},
			},
			{
				id: "disk",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Disk" />
				),
				cell: ({ row }) => {
					const s = row.original.snapshot;
					if (!(s?.diskTotalGB || s?.diskFreeGB != null)) {
						return <span className="text-xs text-muted-foreground">—</span>;
					}
					return (
						<UsageBar
							used={
								typeof s?.diskTotalGB === "number" &&
								typeof s?.diskFreeGB === "number"
									? s.diskTotalGB - s.diskFreeGB
									: undefined
							}
							total={s?.diskTotalGB ?? null}
							unit="GB"
							fallbackLabel={
								s?.diskFreeGB != null
									? `${s.diskFreeGB.toFixed(0)} GB free`
									: "—"
							}
						/>
					);
				},
			},
			{
				id: "api",
				header: "API version",
				cell: ({ row }) => (
					<div className="flex items-center gap-2">
						<span className="font-mono text-xs tabular-nums">
							{row.original.snapshot?.apiVersion ?? "—"}
						</span>
						<DriftBadge
							installedBuilds={row.original.snapshot?.installedBuilds}
							latestBuilds={row.original.snapshot?.latestBuilds}
						/>
					</div>
				),
				enableSorting: false,
			},
			{
				id: "lastPoll",
				header: "Last poll",
				cell: ({ row }) => (
					<span className="text-xs text-muted-foreground">
						{formatRelative(row.original.snapshot?.collectedAt ?? null)}
					</span>
				),
				enableSorting: false,
			},
			{
				id: "actions",
				header: "",
				cell: ({ row }) =>
					canEdit ? (
						<WorkerRowActions
							worker={row.original}
							onEdit={() => setEditing(row.original)}
							onRemove={() => setRemoving(row.original)}
						/>
					) : null,
				enableSorting: false,
				enableHiding: false,
			},
		],
		[canEdit],
	);

	const { table } = useDataTable({
		data: pageRows,
		columns,
		pageCount: Math.max(1, Math.ceil(filtered.length / perPage)),
		initialState: {
			pagination: { pageIndex: page - 1, pageSize: perPage },
			sorting: DEFAULT_SORTING,
			// Three columns worth having and not worth the width by default: the API
			// version only matters during a roll, "last poll" only matters when the
			// Status column has already said something is wrong, and Placement is
			// already on every row as a badge — this column is here for the facet to
			// bind to and for sorting a mixed fleet. Region only matters to fleets
			// that use geo routing.
			columnVisibility: {
				api: false,
				lastPoll: false,
				placement: false,
				region: false,
			},
		},
		getRowId: (row) => row.hostname,
		clearOnDefault: true,
	});

	const isFiltered =
		!!search || !!statusFilter?.length || !!placementFilter?.length;

	return (
		<div className="space-y-6">
			<WorkersCommandBar
				hosts={hosts}
				loading={workers.isLoading}
				canPoll={canEdit}
				canAdd={canEdit}
				pollPending={pollNow.isPending}
				onPoll={() => pollNow.mutate()}
				onAdd={() => navigate("/workers/new")}
				clusterName={cluster.data?.cluster?.name ?? "cluster"}
				clusterStats={cluster.data?.stats}
			/>

			<WorkerViewTabs active="workers" />

			<TooltipProvider delayDuration={150}>
				<DataTable
					table={table}
					isLoading={workers.isLoading}
					loadingRowCount={6}
					loadingFilterCount={3}
					emptyState={
						isFiltered ? (
							<EmptyState
								icon={Server}
								title="No workers match these filters"
								description="Try clearing the status or placement filter, or widening the search."
								action={
									<Button
										variant="outline"
										size="sm"
										onClick={() => {
											table.resetColumnFilters();
											void setSearch("");
										}}
									>
										Clear filters
									</Button>
								}
							/>
						) : (
							<EmptyState
								icon={Server}
								title="No workers yet"
								description="A worker launches and holds the browsers. Add one on this machine, or register one that is already running elsewhere."
								action={
									canEdit ? (
										<div className="flex flex-wrap items-center justify-center gap-2">
											<Button size="sm" asChild>
												<Link href="/workers/new">
													<Plus data-icon="inline-start" /> Add worker
												</Link>
											</Button>
											{/* Straight to the SSH path: an empty fleet on a
											    manager worth running is usually about to get a
											    worker of its own box, not one sharing this one. */}
											<Button size="sm" variant="outline" asChild>
												<Link href="/workers/new?target=remote">
													On another machine
												</Link>
											</Button>
										</div>
									) : undefined
								}
							/>
						)
					}
				>
					<div className="flex flex-wrap items-center gap-2">
						<div className="relative w-full max-w-xs">
							<Search
								className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground"
								aria-hidden="true"
							/>
							<Input
								type="search"
								value={search}
								onChange={(e) => {
									void setSearch(e.target.value);
									void setPage(1);
								}}
								placeholder="Filter by hostname or IP…"
								className="h-8 pl-8"
								aria-label="Filter workers"
							/>
						</div>
						<DataTableToolbar table={table} className="flex-1 p-0" />
					</div>
				</DataTable>
			</TooltipProvider>

			<HostFormDialog
				key={editing?.hostname ?? "edit-closed"}
				open={!!editing}
				onOpenChange={(o) => {
					if (!o) setEditing(null);
				}}
				host={editing}
			/>
			<DeleteWorkerDialog
				worker={removing}
				onOpenChange={(o) => {
					if (!o) setRemoving(null);
				}}
			/>
		</div>
	);
}

function MemTooltipContent({
	runtime,
}: {
	runtime: NonNullable<Snapshot["runtime"]>;
}) {
	const mem = runtime.mem;
	if (!mem) {
		return (
			<div className="space-y-0.5 font-mono tabular-nums">
				<div>No memory data</div>
			</div>
		);
	}
	const KB = 1024 * 1024;
	const totalGiB = mem.totalKB / KB;
	const usedGiB = (mem.totalKB - mem.availableKB) / KB;
	const buffersGiB = mem.buffersKB / KB;
	const cachedGiB = mem.cachedKB / KB;
	const swapTotalGiB = mem.swapTotalKB / KB;
	const swapUsedGiB = (mem.swapTotalKB - mem.swapFreeKB) / KB;
	return (
		<div className="space-y-1 font-mono text-[11px] tabular-nums">
			<div className="font-medium">Memory</div>
			<div>
				{usedGiB.toFixed(1)} / {totalGiB.toFixed(1)} GiB used
			</div>
			{buffersGiB > 0 && (
				<div className="text-muted">buffers {buffersGiB.toFixed(1)} GiB</div>
			)}
			{cachedGiB > 0 && (
				<div className="text-muted">cached {cachedGiB.toFixed(1)} GiB</div>
			)}
			{swapTotalGiB > 0 && (
				<div className="text-muted">
					swap {swapUsedGiB.toFixed(1)} / {swapTotalGiB.toFixed(1)} GiB
				</div>
			)}
		</div>
	);
}

function formatHHMM(collectedAt: string): string {
	const d = new Date(collectedAt);
	return d.toLocaleTimeString([], {
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	});
}

interface ManifestEntry {
	url?: string;
	releaseDate?: string;
}
interface ManifestMajorBlock {
	latestBuild?: string | null;
	activeProfilesVersion?: string | null;
	latestProfilesVersion?: string | null;
	builds?: Record<string, ManifestEntry>;
	profiles?: Record<string, ManifestEntry>;
}
interface ManifestResponse {
	success: boolean;
	manifest: { browserMajors: Record<string, ManifestMajorBlock> } | null;
}

function WorkerDetail({ hostname }: { hostname: string }) {
	const { user } = useAuth();
	const canInstall = hasRole(user, "operator");
	const canLaunch = canInstall;
	const { range, setPreset, setCustom } = useFleetRange();
	const history = useFleetHostHistory(hostname, range);
	const { live, connected } = useFleetLive(hostname);

	// One worker, one page: the load-balancer half of this row used to live on a
	// separate page, so answering "why is this worker getting no traffic" meant
	// two tabs.
	const workers = useQuery({
		queryKey: ["workers"],
		queryFn: () => unwrap(api.api.workers.get()) as Promise<WorkersResponse>,
		refetchInterval: FLEET_STATUS_REFETCH_MS,
	});
	const worker = workers.data?.hosts.find((h) => h.hostname === hostname);
	const [editing, setEditing] = useState(false);

	const stopSession = useMutation({
		mutationFn: async (sessionId: string) => {
			const res = await fetch(
				`/api/workers/${encodeURIComponent(hostname)}/sessions/stop`,
				{
					method: "POST",
					credentials: "include",
					headers: { "content-type": "application/json" },
					body: JSON.stringify({ sessionId }),
				},
			);
			const data = await res.json();
			if (!res.ok || !data.success) {
				throw new Error(data?.error?.message ?? `Stop failed (${res.status})`);
			}
			return data;
		},
		onSuccess: () => {
			toast.success("Stop requested");
			void history.refetch();
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Stop failed"),
	});

	// Stopping kills a live browser and loses whatever it was doing, so it
	// gets the same confirm treatment as the other destructive actions.
	const [confirmStop, setConfirmStop] = useState<StopSessionTarget | null>(
		null,
	);

	const snapshots = history.data?.snapshots ?? [];
	// Snapshots come back in chronological order from the backend now.
	// Prefer the server-provided `latest` (the true newest poll, never
	// downsampled) for the summary cards; fall back to the series tail when
	// the field is absent. `snapshots` stays as-is for the charts.
	const latest =
		history.data?.latest ??
		(snapshots.length > 0 ? snapshots[snapshots.length - 1] : undefined);
	const snapshotRt = latest?.runtime;

	// Live overlay: prefer SSE values when the stream is healthy, fall
	// back to the most recent snapshot. This is what makes the page feel
	// instant — the chart is on a 30s tick, but the cards above pulse at 2s.
	const liveCpu = live?.cpu ?? snapshotRt?.cpu ?? null;
	const liveMem = toMemMetrics(live?.mem ?? snapshotRt?.mem);
	const liveLoad1 = live?.loadAvg1m ?? snapshotRt?.loadAvg1m;
	const liveLoad5 = live?.loadAvg5m ?? snapshotRt?.loadAvg5m;
	const liveLoad15 = live?.loadAvg15m ?? snapshotRt?.loadAvg15m;

	const chartData = useMemo(
		() =>
			snapshots.length > 0
				? snapshots.map((s) => ({
						time: formatHHMM(s.collectedAt),
						activeSessions: s.runtime?.activeSessions ?? null,
						loadAvg1m: s.runtime?.loadAvg1m ?? null,
						memUsedGB: memUsedGiB(s.runtime),
					}))
				: [],
		[snapshots],
	);

	const aggregates = history.data?.aggregates;
	const rangeLabel = range.label;
	const liveIndicatorVisible = range.live && connected;

	const showEarliestBanner =
		!!aggregates?.earliestAvailable &&
		new Date(aggregates.earliestAvailable).getTime() >
			new Date(aggregates.windowFrom).getTime();

	return (
		<div className="space-y-6">
			<div className="flex flex-col gap-2">
				<Link
					href="/workers"
					className="text-sm text-muted-foreground hover:underline"
				>
					← back to workers
				</Link>
				<div className="flex flex-wrap items-center justify-between gap-3">
					<div className="flex items-center gap-3">
						<h1 className="text-2xl font-semibold tracking-tight">
							{hostname}
						</h1>
						{liveIndicatorVisible && (
							<span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
								<span className="relative inline-flex size-2">
									<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
									<span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
								</span>
								live
							</span>
						)}
					</div>
					<RangeFilter
						value={range}
						onPreset={setPreset}
						onCustom={setCustom}
					/>
				</div>
			</div>

			{showEarliestBanner && (
				<Alert>
					<Info />
					<AlertDescription>
						Data only available since{" "}
						{new Date(aggregates.earliestAvailable as string).toLocaleString()}.
						Earlier snapshots have been pruned.
					</AlertDescription>
				</Alert>
			)}

			{worker ? (
				<LoadBalancerCard
					worker={worker}
					canEdit={canInstall}
					onEdit={() => setEditing(true)}
				/>
			) : null}

			<Card>
				<CardHeader className="pb-2">
					<CardTitle className="text-sm font-medium text-muted-foreground">
						CPU (per core)
					</CardTitle>
				</CardHeader>
				<CardContent>
					<CpuStrip cpu={liveCpu} />
				</CardContent>
			</Card>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground">
							Sessions ({rangeLabel})
						</CardTitle>
					</CardHeader>
					<CardContent
						className={cn(
							"p-0",
							chartData.length === 0 ? "px-6 pb-6" : "pb-3 pr-3",
						)}
					>
						{chartData.length === 0 ? (
							<Empty className="border-0">
								<EmptyHeader>
									<EmptyMedia variant="icon">
										<CpuIcon />
									</EmptyMedia>
									<EmptyTitle>No samples in window</EmptyTitle>
									<EmptyDescription>
										Pick a wider range or wait for the next poll.
									</EmptyDescription>
								</EmptyHeader>
							</Empty>
						) : (
							<ResponsiveContainer width="100%" height={140}>
								<AreaChart
									data={chartData}
									margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
								>
									<defs>
										<linearGradient id="sessGrad" x1="0" y1="0" x2="0" y2="1">
											<stop offset="5%" stopColor="#22c55e" stopOpacity={0.2} />
											<stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
										</linearGradient>
									</defs>
									<CartesianGrid
										strokeDasharray="3 3"
										stroke="hsl(var(--border))"
									/>
									<XAxis
										dataKey="time"
										tick={{
											fontSize: 10,
											fill: "hsl(var(--muted-foreground))",
										}}
										interval="preserveStartEnd"
										tickLine={false}
										axisLine={false}
									/>
									<YAxis
										tick={{
											fontSize: 10,
											fill: "hsl(var(--muted-foreground))",
										}}
										tickLine={false}
										axisLine={false}
										allowDecimals={false}
									/>
									<RechartsTooltip
										contentStyle={{
											fontSize: 12,
											borderRadius: 6,
											border: "1px solid hsl(var(--border))",
											background: "hsl(var(--popover))",
											color: "hsl(var(--popover-foreground))",
										}}
										formatter={(v) => [v, "sessions"]}
									/>
									<Area
										type="monotone"
										dataKey="activeSessions"
										stroke="#22c55e"
										strokeWidth={2}
										fill="url(#sessGrad)"
										dot={false}
										connectNulls
									/>
								</AreaChart>
							</ResponsiveContainer>
						)}
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground">
							Load &amp; Memory ({rangeLabel})
						</CardTitle>
					</CardHeader>
					<CardContent
						className={cn(
							"p-0",
							chartData.length === 0 ? "px-6 pb-6" : "pb-3 pr-3",
						)}
					>
						{chartData.length === 0 ? (
							<Empty className="border-0">
								<EmptyHeader>
									<EmptyMedia variant="icon">
										<CpuIcon />
									</EmptyMedia>
									<EmptyTitle>No samples in window</EmptyTitle>
									<EmptyDescription>
										Pick a wider range or wait for the next poll.
									</EmptyDescription>
								</EmptyHeader>
							</Empty>
						) : (
							<ResponsiveContainer width="100%" height={140}>
								<AreaChart
									data={chartData}
									margin={{ top: 4, right: 36, left: -20, bottom: 0 }}
								>
									<defs>
										<linearGradient id="loadGrad" x1="0" y1="0" x2="0" y2="1">
											<stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2} />
											<stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
										</linearGradient>
										<linearGradient id="memGrad" x1="0" y1="0" x2="0" y2="1">
											<stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
											<stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
										</linearGradient>
									</defs>
									<CartesianGrid
										strokeDasharray="3 3"
										stroke="hsl(var(--border))"
									/>
									<XAxis
										dataKey="time"
										tick={{
											fontSize: 10,
											fill: "hsl(var(--muted-foreground))",
										}}
										interval="preserveStartEnd"
										tickLine={false}
										axisLine={false}
									/>
									<YAxis
										yAxisId="load"
										tick={{
											fontSize: 10,
											fill: "hsl(var(--muted-foreground))",
										}}
										tickLine={false}
										axisLine={false}
									/>
									<YAxis
										yAxisId="mem"
										orientation="right"
										tick={{
											fontSize: 10,
											fill: "hsl(var(--muted-foreground))",
										}}
										tickLine={false}
										axisLine={false}
									/>
									<RechartsTooltip
										contentStyle={{
											fontSize: 12,
											borderRadius: 6,
											border: "1px solid hsl(var(--border))",
											background: "hsl(var(--popover))",
											color: "hsl(var(--popover-foreground))",
										}}
										formatter={(v, name) => [
											typeof v === "number"
												? name === "loadAvg1m"
													? v.toFixed(2)
													: `${v.toFixed(1)} GB`
												: v,
											name === "loadAvg1m" ? "load" : "mem",
										]}
									/>
									<Area
										yAxisId="load"
										type="monotone"
										dataKey="loadAvg1m"
										stroke="#f59e0b"
										strokeWidth={2}
										fill="url(#loadGrad)"
										dot={false}
										connectNulls
									/>
									<Area
										yAxisId="mem"
										type="monotone"
										dataKey="memUsedGB"
										stroke="#3b82f6"
										strokeWidth={2}
										fill="url(#memGrad)"
										dot={false}
										connectNulls
									/>
								</AreaChart>
							</ResponsiveContainer>
						)}
					</CardContent>
				</Card>
			</div>

			{snapshotRt && (
				<Card>
					<CardHeader>
						<CardTitle className="text-base flex items-center gap-2">
							<CpuIcon className="h-4 w-4" /> Runtime
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="grid grid-cols-1 gap-x-8 gap-y-4 text-sm md:grid-cols-2 xl:grid-cols-4">
							<div>
								<div className="text-xs uppercase text-muted-foreground">
									Active sessions
								</div>
								<div className="font-semibold text-lg tabular-nums">
									{live?.activeSessions ?? snapshotRt.activeSessions}
								</div>
							</div>
							{aggregates && (
								<PeakConcurrentStat
									peak={aggregates.peakConcurrent}
									peakAt={aggregates.peakConcurrentAt}
									rangeLabel={rangeLabel}
								/>
							)}
							<div>
								<div className="text-xs uppercase text-muted-foreground">
									Today
								</div>
								<div className="font-semibold text-lg tabular-nums">
									{snapshotRt.sessionsToday}
								</div>
								<div className="text-xs text-muted-foreground">
									{snapshotRt.activeSessions} running,{" "}
									{snapshotRt.sessionsToday - snapshotRt.activeSessions}{" "}
									finished
								</div>
							</div>
							<div>
								<div className="text-xs uppercase text-muted-foreground">
									Last 24h
								</div>
								<div className="font-semibold text-lg tabular-nums">
									{snapshotRt.sessionsLast24h}
								</div>
							</div>
							<LoadReadout
								loadAvg1m={liveLoad1}
								loadAvg5m={liveLoad5}
								loadAvg15m={liveLoad15}
							/>
							<div className="md:col-span-2 xl:col-span-2">
								<MemoryBar mem={liveMem} />
							</div>
							<div>
								<div className="text-xs uppercase text-muted-foreground">
									Profile index
								</div>
								<div className="font-semibold">
									{snapshotRt.profileIndex.count.toLocaleString()} entries
								</div>
								<div className="text-xs text-muted-foreground">
									{snapshotRt.profileIndex.ageSec != null
										? `refreshed ${snapshotRt.profileIndex.ageSec}s ago`
										: "not built"}
								</div>
							</div>
						</div>

						<DisplayPool live={live?.displays} snapshot={snapshotRt.displays} />

						{canLaunch ? (
							<LaunchProfileCard
								hostname={hostname}
								inventory={{
									profileOsesByMajor: latest?.installedProfileOses ?? null,
									profilesByMajor: latest?.installedProfiles ?? null,
								}}
							/>
						) : null}

						{snapshotRt.recentLaunches.length > 0 && (
							<div className="mt-4">
								<div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-4">
									<div className="text-xs uppercase text-muted-foreground">
										Recent launches
									</div>
									{/* This table is a snapshot, not a live view, and it has
									    shown browsers as "running" that exited hours earlier.
									    Saying when it was taken is the difference between a
									    stale row and a lie. */}
									{latest ? (
										<div className="text-xs text-muted-foreground">
											as of {formatRelative(latest.collectedAt)}
										</div>
									) : null}
								</div>
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Browser</TableHead>
											<TableHead>Status</TableHead>
											<TableHead>Started</TableHead>
											{canLaunch && <TableHead className="w-16" />}
										</TableRow>
									</TableHeader>
									<TableBody>
										{snapshotRt.recentLaunches.map((l) => {
											const identity = describeSession(
												fingerprintFromBundlePath(l.profile),
											);
											return (
												<TableRow key={l.id}>
													<TableCell className="max-w-[280px]">
														{/* The bundle path is still the truth of record,
														    so it stays reachable on hover — it just
														    stops being the thing an operator has to
														    read to tell two rows apart. */}
														<div
															className="truncate text-xs font-medium"
															title={l.profile ?? undefined}
														>
															{identity.label ?? "Unidentified profile"}
														</div>
														<div className="truncate font-mono text-[0.7rem] text-muted-foreground">
															{identity.hash
																? `${identity.hash} · ${shortId(l.id)}`
																: shortId(l.id)}
														</div>
													</TableCell>
													<TableCell>
														<Badge
															variant={
																l.status === "running" ? "success" : "outline"
															}
															className="text-[10px]"
														>
															{l.status}
														</Badge>
													</TableCell>
													<TableCell className="text-xs">
														{formatRelative(l.startedAt)}
													</TableCell>
													{canLaunch && (
														<TableCell>
															{l.status === "running" && (
																<Button
																	variant="ghost"
																	size="sm"
																	disabled={stopSession.isPending}
																	aria-label={`Stop ${identity.label ?? "session"} on ${hostname}`}
																	onClick={() =>
																		setConfirmStop({
																			kind: "session",
																			hostname,
																			id: l.id,
																			profilePath: l.profile ?? null,
																			startedAt: l.startedAt,
																		})
																	}
																>
																	Stop
																</Button>
															)}
														</TableCell>
													)}
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</div>
						)}
					</CardContent>
				</Card>
			)}

			{canInstall && (
				<BundleSyncCard
					hostname={hostname}
					lastSyncAt={latest?.lastBundleSyncAt ?? null}
				/>
			)}

			{canInstall && latest && (
				<InstallableVersions hostname={hostname} latest={latest} />
			)}

			{latest && (
				<Card>
					<CardHeader>
						<CardTitle className="text-base flex items-center gap-2">
							<HardDrive className="h-4 w-4" /> Installed versions
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4 text-sm">
						<div className="flex items-center gap-4">
							<span className="text-muted-foreground">Collected</span>
							{formatRelative(latest.collectedAt)}
						</div>
						<div className="flex items-center gap-4">
							<span className="text-muted-foreground">API</span>
							<Badge variant="outline">{latest.apiVersion ?? "—"}</Badge>
						</div>
						<InstalledInventory latest={latest} />
					</CardContent>
				</Card>
			)}

			<Card>
				<CardHeader>
					<CardTitle className="text-base">Timeline</CardTitle>
				</CardHeader>
				<CardContent className="p-0">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Collected</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>API</TableHead>
								<TableHead>Sessions</TableHead>
								<TableHead>Load</TableHead>
								<TableHead>Disk</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{[...(history.data?.snapshots ?? [])].reverse().map((s) => (
								<TableRow key={s.id}>
									<TableCell className="text-xs">
										{formatRelative(s.collectedAt)}
									</TableCell>
									<TableCell>
										<ReachabilityBadge snapshot={s} />
									</TableCell>
									<TableCell className="text-xs">
										{s.apiVersion ?? "—"}
									</TableCell>
									<TableCell className="font-mono text-xs">
										{s.runtime
											? `${s.runtime.activeSessions} / ${s.runtime.sessionsToday}`
											: "—"}
									</TableCell>
									<TableCell className="text-xs">
										{s.runtime ? (
											<LoadIndicator load={s.runtime.loadAvg1m} />
										) : (
											"—"
										)}
									</TableCell>
									<TableCell className="text-xs">
										{s.diskFreeGB != null
											? `${s.diskFreeGB.toFixed(0)} GB`
											: "—"}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			<StopSessionDialog
				target={confirmStop}
				pending={stopSession.isPending}
				onOpenChange={() => setConfirmStop(null)}
				onConfirm={(target) =>
					stopSession.mutate(target.id, {
						onSettled: () => setConfirmStop(null),
					})
				}
			/>

			<HostFormDialog
				key={editing ? "edit-open" : "edit-closed"}
				open={editing}
				onOpenChange={setEditing}
				host={worker ?? null}
			/>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Load-balancer config for one worker
// ---------------------------------------------------------------------------

/**
 * How the load balancer treats this worker.
 *
 * This is what `/cluster/pool` existed to show, on the page that already
 * describes the worker. Read-only at rest with one Edit button, because the
 * fields an operator changes in passing — pool membership and weight — are
 * live in the table's "In pool" column and everything else here is set once.
 */
function LoadBalancerCard({
	worker,
	canEdit,
	onEdit,
}: {
	worker: WorkerRow;
	canEdit: boolean;
	onEdit: () => void;
}) {
	return (
		<Card>
			<CardHeader className="flex flex-row items-start justify-between gap-3">
				<CardTitle className="text-base flex items-center gap-2">
					<Network className="h-4 w-4" /> Load balancer
				</CardTitle>
				<div className="flex items-center gap-2">
					<WorkerPoolCell worker={worker} canEdit={canEdit} />
					{canEdit ? (
						<Button variant="outline" size="sm" onClick={onEdit}>
							Edit
						</Button>
					) : null}
				</div>
			</CardHeader>
			<CardContent>
				<dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm md:grid-cols-4">
					<ConfigField
						label="Upstream"
						value={`${worker.publicIp}:${worker.lbPort}`}
					/>
					<ConfigField label="Base path" value={worker.basePath || "/"} />
					<ConfigField label="Status endpoint" value={worker.statusEndpoint} />
					<ConfigField label="Probe port" value={String(worker.probePort)} />
					<ConfigField
						label="API key"
						value={worker.apiKey ? "set" : "not set"}
					/>
					<ConfigField
						label="Updater agent"
						value={worker.hasUpdaterAgent ? "configured" : "none"}
					/>
					{/* Placement and "managed here" are next to each other because they
					    are easy to confuse and are not the same question: one is where
					    the worker runs, the other is whether this manager can drive its
					    container over its own docker socket. */}
					<ConfigField
						label="Runs on"
						value={
							worker.placement === "local" ? "this machine" : "own machine"
						}
					/>
					<ConfigField
						label="Managed here"
						value={worker.managed ? "yes" : "registered only"}
					/>
					<ConfigField label="Weight" value={String(worker.weight)} />
				</dl>
				{worker.notes ? (
					<p className="mt-4 whitespace-pre-wrap text-sm text-muted-foreground">
						{worker.notes}
					</p>
				) : null}
			</CardContent>
		</Card>
	);
}

function ConfigField({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<dt className="text-xs uppercase text-muted-foreground">{label}</dt>
			<dd className="font-mono text-xs tabular-nums">{value}</dd>
		</div>
	);
}

// ---------------------------------------------------------------------------
// "Available to install" panel for a single worker
// ---------------------------------------------------------------------------

interface InstallableRow {
	kind: "chromium" | "profiles";
	major: number;
	version: string;
	releaseDate?: string;
	isLatest: boolean;
	isActive: boolean;
}

interface RunningOpRow {
	id: string;
	kind: string;
	status: string;
	inputs: Record<string, unknown>;
}

interface RunningOpsResponse {
	success: boolean;
	data: RunningOpRow[];
}

// Action cell shared by the Chromium + Profiles tables. Three visual states:
//   - installing: a link to the live operation (persists across refresh,
//     driven by the running-ops query, not local state)
//   - dispatching: the brief window after click before the op is polled in
//   - idle: the install button, disabled when the host is otherwise busy
function InstallActionCell({
	installingOpId,
	dispatching,
	disabled,
	hostBusy,
	onInstall,
}: {
	installingOpId: string | undefined;
	dispatching: boolean;
	disabled: boolean;
	hostBusy: boolean;
	onInstall: () => void;
}) {
	if (installingOpId) {
		return (
			<Button size="sm" variant="outline" asChild>
				<Link href={`/operations/${installingOpId}`}>
					<Loader2 className="mr-1 h-3 w-3 animate-spin" />
					Installing…
				</Link>
			</Button>
		);
	}
	return (
		<Button
			size="sm"
			variant="outline"
			disabled={disabled}
			title={
				hostBusy && !dispatching
					? "An install is already in progress on this host"
					: undefined
			}
			onClick={onInstall}
		>
			{dispatching ? (
				<>
					<Loader2 className="mr-1 h-3 w-3 animate-spin" />
					dispatching…
				</>
			) : (
				<>
					<Download className="mr-1 h-3 w-3" />
					install
				</>
			)}
		</Button>
	);
}

function InstallableVersions({
	hostname,
	latest,
}: {
	hostname: string;
	latest: Snapshot;
}) {
	const queryClient = useQueryClient();
	const [pending, setPending] = useState<string | null>(null);
	const [force, setForce] = useState(false);

	const manifest = useQuery({
		queryKey: ["manifest"],
		queryFn: () => unwrap(api.api.manifest.get()) as Promise<ManifestResponse>,
		refetchInterval: 60_000,
	});

	// Running install/update operations are the durable source of truth for
	// the "Installing…" state — derived from the DB so it survives a refresh
	// instead of living in ephemeral local React state. Polled on a short
	// interval so the indicator clears on its own once the op finalizes.
	const runningOps = useQuery({
		queryKey: ["operations", "running", hostname],
		queryFn: () =>
			unwrap(
				api.api.operations.get({
					query: { status: "running", pageSize: 100 },
				}),
			) as Promise<RunningOpsResponse>,
		refetchInterval: 4_000,
	});

	// Map of `${kind}:${major}:${version}` -> operationId for installs running
	// on THIS host, plus a host-level busy flag. `hostBusy` mirrors the
	// backend per-host advisory lock (`hostLocks` in routes/fleet.ts): while an
	// install or a full run-update is in flight, every other install on the
	// host would 409, so we disable them all.
	const { installingByKey, hostBusy } = useMemo(() => {
		const map = new Map<string, string>();
		let busy = false;
		for (const op of runningOps.data?.data ?? []) {
			const inputs = op.inputs as {
				hostname?: unknown;
				kind?: unknown;
				major?: unknown;
				version?: unknown;
			};
			if (inputs?.hostname !== hostname) continue;
			if (
				op.kind === "fleet-install-chromium" ||
				op.kind === "fleet-install-profiles"
			) {
				busy = true;
				const { kind, major, version } = inputs;
				if (
					typeof kind === "string" &&
					(typeof major === "number" || typeof major === "string") &&
					typeof version === "string"
				) {
					map.set(`${kind}:${major}:${version}`, op.id);
				}
			} else if (op.kind === "fleet-run-update") {
				busy = true;
			}
		}
		return { installingByKey: map, hostBusy: busy };
	}, [runningOps.data, hostname]);

	// When a previously-running install key disappears between polls the op
	// finalized; refresh the fleet snapshot + manifest so the freshly-installed
	// version drops out of the available rows without waiting for the slow
	// 60s manifest poll.
	const prevRunningKeysRef = useRef<Set<string>>(new Set());
	useEffect(() => {
		const current = new Set(installingByKey.keys());
		const prev = prevRunningKeysRef.current;
		let cleared = false;
		for (const key of prev) {
			if (!current.has(key)) {
				cleared = true;
				break;
			}
		}
		prevRunningKeysRef.current = current;
		if (cleared) {
			queryClient.invalidateQueries({ queryKey: ["workers"] });
			queryClient.invalidateQueries({ queryKey: ["manifest"] });
		}
	}, [installingByKey, queryClient]);

	const { chromiumRows, profileRows } = useMemo(() => {
		const m = manifest.data?.manifest;
		if (!m) {
			return {
				chromiumRows: [] as InstallableRow[],
				profileRows: [] as InstallableRow[],
			};
		}
		const installedBuilds = latest.installedBuilds ?? {};
		const installedProfiles = latest.installedProfiles ?? {};
		const chromiumRows: InstallableRow[] = [];
		const profileRows: InstallableRow[] = [];
		for (const [majorStr, block] of Object.entries(m.browserMajors)) {
			const major = Number(majorStr);
			if (!Number.isFinite(major)) continue;
			const ownedBuilds = new Set(installedBuilds[majorStr] ?? []);
			const ownedProfiles = new Set(installedProfiles[majorStr] ?? []);
			for (const [version, entry] of Object.entries(block.builds ?? {})) {
				if (ownedBuilds.has(version)) continue;
				chromiumRows.push({
					kind: "chromium",
					major,
					version,
					releaseDate: entry.releaseDate,
					isLatest: block.latestBuild === version,
					isActive: false,
				});
			}
			for (const [version, entry] of Object.entries(block.profiles ?? {})) {
				if (ownedProfiles.has(version)) continue;
				profileRows.push({
					kind: "profiles",
					major,
					version,
					releaseDate: entry.releaseDate,
					isLatest: block.latestProfilesVersion === version,
					isActive: block.activeProfilesVersion === version,
				});
			}
		}
		const sortRows = (a: InstallableRow, b: InstallableRow) => {
			if (a.kind !== b.kind) return a.kind === "chromium" ? -1 : 1;
			if (a.major !== b.major) return b.major - a.major;
			return compareSemver(b.version, a.version);
		};
		chromiumRows.sort(sortRows);
		profileRows.sort(sortRows);
		return { chromiumRows, profileRows };
	}, [manifest.data, latest.installedBuilds, latest.installedProfiles]);
	const totalRows = chromiumRows.length + profileRows.length;

	const install = useMutation({
		mutationFn: async (row: InstallableRow) => {
			const res = await fetch(
				`/api/workers/${encodeURIComponent(hostname)}/install`,
				{
					method: "POST",
					credentials: "include",
					headers: { "content-type": "application/json" },
					body: JSON.stringify({
						kind: row.kind,
						major: row.major,
						version: row.version,
						force,
					}),
				},
			);
			const data = (await res.json()) as {
				success?: boolean;
				operationId?: string;
				error?: { message?: string; code?: string };
			};
			if (!res.ok || !data.success || !data.operationId) {
				throw new Error(data.error?.message ?? `HTTP ${res.status}`);
			}
			return data.operationId;
		},
		onMutate: (row) => {
			setPending(`${row.kind}:${row.major}:${row.version}`);
		},
		onSuccess: () => {
			toast.success("Install dispatched");
			// Stay on the host page. Refresh the running-ops query so the new
			// operation surfaces as "Installing…" immediately (before the 4s
			// poll), and the fleet view so any mounted strip reflects the
			// in-flight work.
			queryClient.invalidateQueries({
				queryKey: ["operations", "running", hostname],
			});
			queryClient.invalidateQueries({ queryKey: ["workers"] });
		},
		onError: (err) => {
			toast.error(err instanceof Error ? err.message : "Install failed");
			setPending(null);
		},
	});

	if (manifest.isLoading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="text-base flex items-center gap-2">
						<Download className="h-4 w-4" /> Available to install
					</CardTitle>
				</CardHeader>
				<CardContent>
					<Skeleton className="h-20" />
				</CardContent>
			</Card>
		);
	}

	if (totalRows === 0) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="text-base flex items-center gap-2">
						<Download className="h-4 w-4" /> Available to install
					</CardTitle>
				</CardHeader>
				<CardContent className="text-sm text-muted-foreground">
					This worker is up to date with every release in the manifest.
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader className="flex flex-row items-start justify-between gap-3">
				<div>
					<CardTitle className="text-base flex items-center gap-2">
						<Download className="h-4 w-4" /> Available to install
					</CardTitle>
					<p className="text-xs text-muted-foreground mt-1">
						Manifest entries this worker has not reported yet. Installs are
						queued one at a time per host.
					</p>
				</div>
				<div className="flex items-center gap-2 text-xs text-muted-foreground select-none">
					<Switch
						size="sm"
						checked={force}
						onCheckedChange={(checked) => setForce(Boolean(checked))}
						aria-label="Force re-install"
					/>
					<span>Force re-install</span>
				</div>
			</CardHeader>
			<CardContent className="space-y-3">
				<Tabs
					defaultValue={chromiumRows.length > 0 ? "chromium" : "profiles"}
					className="gap-3"
				>
					<TabsList>
						<TabsTrigger value="chromium">
							Chromium
							<Badge variant="outline" className="h-4 px-1.5 text-[10px]">
								{chromiumRows.length}
							</Badge>
						</TabsTrigger>
						<TabsTrigger value="profiles">
							Profiles
							<Badge variant="outline" className="h-4 px-1.5 text-[10px]">
								{profileRows.length}
							</Badge>
						</TabsTrigger>
					</TabsList>
					<TabsContent value="chromium">
						{chromiumRows.length === 0 ? (
							<p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
								No installable Chromium versions for this worker.
							</p>
						) : (
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Major</TableHead>
										<TableHead>Version</TableHead>
										<TableHead>Released</TableHead>
										<TableHead>Status</TableHead>
										<TableHead className="text-right">Action</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{chromiumRows.map((r) => {
										const key = `${r.kind}:${r.major}:${r.version}`;
										const installingOpId = installingByKey.get(key);
										const dispatching = pending === key && install.isPending;
										return (
											<TableRow key={key}>
												<TableCell className="font-mono text-xs">
													{r.major}
												</TableCell>
												<TableCell className="font-mono text-xs">
													{r.version}
												</TableCell>
												<TableCell className="text-xs text-muted-foreground">
													{r.releaseDate ? formatRelative(r.releaseDate) : "—"}
												</TableCell>
												<TableCell>
													{r.isLatest ? (
														<Badge variant="secondary" className="text-[10px]">
															latest
														</Badge>
													) : (
														<span className="text-xs text-muted-foreground">
															—
														</span>
													)}
												</TableCell>
												<TableCell className="text-right">
													<InstallActionCell
														installingOpId={installingOpId}
														dispatching={dispatching}
														disabled={
															hostBusy || install.isPending || dispatching
														}
														hostBusy={hostBusy}
														onInstall={() => install.mutate(r)}
													/>
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						)}
					</TabsContent>
					<TabsContent value="profiles">
						{profileRows.length === 0 ? (
							<p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
								No installable profile bundles for this worker.
							</p>
						) : (
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Major</TableHead>
										<TableHead>Version</TableHead>
										<TableHead>Released</TableHead>
										<TableHead>Status</TableHead>
										<TableHead className="text-right">Action</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{profileRows.map((r) => {
										const key = `${r.kind}:${r.major}:${r.version}`;
										const installingOpId = installingByKey.get(key);
										const dispatching = pending === key && install.isPending;
										return (
											<TableRow key={key}>
												<TableCell className="font-mono text-xs">
													{r.major}
												</TableCell>
												<TableCell className="font-mono text-xs">
													{r.version}
												</TableCell>
												<TableCell className="text-xs text-muted-foreground">
													{r.releaseDate ? formatRelative(r.releaseDate) : "—"}
												</TableCell>
												<TableCell>
													<div className="flex items-center gap-1">
														{r.isLatest && (
															<Badge
																variant="secondary"
																className="text-[10px]"
															>
																latest
															</Badge>
														)}
														{r.isActive && (
															<Badge variant="success" className="text-[10px]">
																active
															</Badge>
														)}
														{!r.isLatest && !r.isActive && (
															<span className="text-xs text-muted-foreground">
																—
															</span>
														)}
													</div>
												</TableCell>
												<TableCell className="text-right">
													<InstallActionCell
														installingOpId={installingOpId}
														dispatching={dispatching}
														disabled={
															hostBusy || install.isPending || dispatching
														}
														hostBusy={hostBusy}
														onInstall={() => install.mutate(r)}
													/>
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						)}
					</TabsContent>
				</Tabs>
			</CardContent>
		</Card>
	);
}
